import { ReactNode } from 'react';
import { css } from '@emotion/react';
import { Icon, IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { ContentLoader, NakedButton, Text, Truncated } from '@frontend/design-system';
import { TruncatableHeaderTitle } from './truncatable-header';

type Summary = {
  label: string;
  value: string | number;
};

interface CardProps {
  iconName?: IconName;
  infoTop?: ReactNode;
  isLoading?: boolean;
  onClick?: () => void;
  summary?: Summary[];
  title: string;
  trackingId?: string;
}

export const SmallModeSummaryCard = ({
  iconName,
  infoTop,
  isLoading,
  onClick,
  summary = [],
  title,
  trackingId,
}: CardProps) => {
  return (
    <NakedButton
      className={`${typeof onClick === 'function' ? 'clickable' : ''}`}
      css={styles.card}
      onClick={onClick}
      trackingId={trackingId}
    >
      <TruncatableHeaderTitle
        leftElement={iconName ? <Icon className='icon' name={iconName} size={16} /> : null}
        title={title}
        infoTip={infoTop}
        style={{ marginBottom: theme.spacing(1) }}
      />
      <div className='summaries'>
        {summary.map(({ label, value }) => (
          <div className={`summary-item count-${summary?.length}`} key={label}>
            <Truncated className='value'>{value}</Truncated>
            <Text color='subdued' size='small'>
              {label}
            </Text>
          </div>
        ))}
      </div>
      <ContentLoader show={isLoading} size='small' />
    </NakedButton>
  );
};

const styles = {
  card: css`
    border-radius: ${theme.borderRadius.medium};
    border: 1px solid ${theme.colors.neutral20};
    flex-basis: calc(50% - ${theme.spacing(1)});
    flex-grow: 1;
    min-width: 150px;
    overflow: hidden;
    padding: ${theme.spacing(2)};
    position: relative;
    cursor: default;

    &.clickable {
      cursor: pointer;

      &:hover {
        background-color: ${theme.colors.neutral5};
      }
    }

    .summaries {
      display: flex;
      justify-content: space-between;
      gap: ${theme.spacing(1)};

      .summary-item {
        align-items: baseline;
        display: flex;
        flex-direction: column;
        width: calc(50% - ${theme.spacing(0.5)});

        &.count-1 {
          width: 100%;
        }

        .value {
          font-size: ${theme.fontSize(24)};
          text-align: left;
          font-weight: bold;
        }
      }
    }

    .icon {
      height: ${theme.spacing(2)};
      min-height: ${theme.spacing(2)};
      min-width: ${theme.spacing(2)};
      width: ${theme.spacing(2)};
    }
  `,
};
